import React, { useState } from "react";
import {
  Box,
  FormControl,
  IconButton,
  InputLabel,
  ListItem,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import theme from "../../../theme/theme";
import { capitalizeFirstLetter } from "../../../utils/strings";
import i18n from "../../../i18n-config";
import {
  CustomAvatar,
  CustomButton,
  CustomPopper,
  CustomTableFooter,
  NoDataTable,
  StyledTableCell,
  StyledTableRow,
  SvgContainer,
} from "../../../components";
import { MoreVert } from "@mui/icons-material";
import { LOCATION, PHONE, SORT } from "../../../assets/icons";
import { STATUT } from "../../../utils/constants";

const style = {
  fontSize: 14,
  color: theme.colors.black,
  fontWeight: 550,
};

const MenuProps = {
  PaperProps: {
    style: {
      paddingRight: 20,
    },
  },
};

interface Props {
  data: any;
  onBlock: (item: any) => void;
  onDetail: (item: any) => void;
}

function BossTable({ data, onBlock, onDetail }: Props) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedStatus, setSelectedStatus] = useState("ALL");

  const handleChangePage = (event: any, newPage: any) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: any) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const onStatusChange = (value: string) => {
    setSelectedStatus(value);
  };

  const items = (
    rowsPerPage > 0
      ? data.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
      : data
  ).filter((val: any) =>
    "ALL" === selectedStatus
      ? val
      : selectedStatus === "ACTIF"
      ? val.actif
      : val.bloquer
  );

  return (
    <TableContainer
      component={Paper}
      sx={{ mb: 2, boxShadow: "0px 4px 4px rgba(139, 139, 139, 0.05)" }}
    >
      <Table sx={{ minWidth: 650 }} aria-label="simple table">
        <TableHead>
          <TableRow sx={{ bgcolor: "#fff" }}>
            <TableCell sx={style}>#</TableCell>
            <TableCell sx={style}>
              <Stack direction={"row"} alignItems="center">
                <Typography mr={1}>
                  {capitalizeFirstLetter(i18n.t("nom") + "s")}
                </Typography>
                <IconButton>
                  <SvgContainer
                    svg={SORT}
                    width={15}
                    color={theme.colors.black}
                  />
                </IconButton>
              </Stack>
            </TableCell>
            <TableCell sx={style}>
              <Stack direction={"row"}>
                <SvgContainer
                  svg={PHONE}
                  width={15}
                  color={theme.colors.dimgray + "80"}
                />
                <Typography ml={1}>
                  {capitalizeFirstLetter(i18n.t("telephone"))}
                </Typography>
              </Stack>
            </TableCell>
            <TableCell sx={style}>
              <Stack direction={"row"}>
                <SvgContainer
                  svg={LOCATION}
                  width={15}
                  color={theme.colors.dimgray + "80"}
                />
                <Typography ml={1}>
                  {capitalizeFirstLetter(i18n.t("localisation"))}
                </Typography>
              </Stack>
            </TableCell>
            <TableCell sx={style}>
              <Stack direction={"row"} alignItems="center">
                <Typography mr={1}>Bara</Typography>
                <IconButton>
                  <SvgContainer
                    svg={SORT}
                    width={15}
                    color={theme.colors.black}
                  />
                </IconButton>
              </Stack>
            </TableCell>
            <TableCell>
              <FormControl fullWidth>
                <InputLabel>{i18n.t("status")}</InputLabel>
                <Select variant="standard" value={selectedStatus}>
                  <MenuItem
                    value={"ALL"}
                    onClick={() => onStatusChange("ALL")}
                    style={{
                      background: "white",
                      color: theme.colors.black,
                    }}
                  >
                    {capitalizeFirstLetter(i18n.t("tout_afficher"))}
                  </MenuItem>
                  {STATUT.map((status: string) => (
                    <MenuItem
                      value={status}
                      onClick={() => onStatusChange(status)}
                      style={{
                        background: "white",
                        color: theme.colors.black,
                      }}
                    >
                      {capitalizeFirstLetter(i18n.t(status))}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </TableCell>
            <TableCell sx={style}></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((item: any, index: any) => (
            <StyledTableRow
              key={index}
              sx={{
                "&:last-child td, &:last-child th": { border: 0 },
                cursor: "pointer",
              }}
            >
              <StyledTableCell>{item.id}</StyledTableCell>
              <StyledTableCell>
                <Stack direction={"row"} alignItems="center">
                  <CustomAvatar
                    rootStyle={{
                      border: "none",
                      marginTop: 0,
                      marginLeft: 0,
                      marginRight: 1.5,
                    }}
                    avatarStyle={{ borderRadius: 100 }}
                  />
                  <Typography>{`${item.nom} ${item.prenom}`}</Typography>
                </Stack>
              </StyledTableCell>
              <StyledTableCell>{item.telephone}</StyledTableCell>
              <StyledTableCell>{item.adresse}</StyledTableCell>
              <StyledTableCell>{item.nombreOffres}</StyledTableCell>
              <StyledTableCell style={{ width: 100 }}>
                <CustomPopper
                  icon={
                    <CustomButton
                      title={capitalizeFirstLetter(
                        i18n.t(item.actif ? "actif" : "bloque")
                      )}
                      rootStyle={{
                        backgroundColor: item.actif
                          ? theme.colors.primary
                          : theme.colors.red,
                        width: "100%",
                      }}
                      titleStyle={{ fontSize: 12, color: "white" }}
                      disabled
                    />
                  }
                  placement="center"
                  containerStyle={{ padding: 0, borderRadius: "5px" }}
                  buttonStyle={{
                    backgroundColor: "transparent",
                    width: "100%",
                    padding: 0,
                  }}
                >
                  <Box
                    p={2}
                    sx={{
                      height: "70px",
                      width: "150px",
                    }}
                  >
                    <ListItem
                      // onClick={() => onStatusChange(status)}
                      style={{
                        cursor: "pointer",
                        color: item.actif
                          ? theme.colors.red
                          : theme.colors.primary,
                      }}
                    >
                      {capitalizeFirstLetter(
                        i18n.t(item.actif ? "bloquer" : "activer")
                      )}
                    </ListItem>
                  </Box>
                </CustomPopper>
              </StyledTableCell>
              <StyledTableCell style={{ width: 40 }}>
                <Select
                  MenuProps={MenuProps}
                  disableUnderline
                  variant="standard"
                  IconComponent={(props) => (
                    <MoreVert
                      {...props}
                      sx={{ border: "1px solid #EAEAEA", borderRadius: 100 }}
                    />
                  )}
                >
                  <MenuItem
                    onClick={() => onDetail(item)}
                    style={{
                      background: "white",
                      color: theme.colors.black,
                    }}
                  >
                    {capitalizeFirstLetter(i18n.t("detail"))}
                  </MenuItem>
                  <MenuItem
                    // onClick={() => onEdit(item)}
                    style={{
                      background: "white",
                      color: theme.colors.black,
                    }}
                  >
                    {capitalizeFirstLetter(i18n.t("creer_bara"))}
                  </MenuItem>
                  <MenuItem
                    // onClick={() => onEdit(item)}
                    style={{
                      background: "white",
                      color: theme.colors.red,
                    }}
                  >
                    {capitalizeFirstLetter(i18n.t("supprimer"))}
                  </MenuItem>
                </Select>
              </StyledTableCell>
            </StyledTableRow>
          ))}
          {!items.length ? <NoDataTable /> : null}
        </TableBody>
        <CustomTableFooter
          rowsPerPage={rowsPerPage}
          data={data}
          page={page}
          handleChangePage={handleChangePage}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
        />
      </Table>
    </TableContainer>
  );
}

export default BossTable;
